import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { PageTitleService } from '../../../shared/services';
import { AuthService } from '../../../services/auth';
import { BusinessService } from '../../../services/business';
import { CountryService } from '../../../services';
import { Professional } from '../../../shared/interfaces';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal';
import {
  CreateInvitationResult,
  InvitationChannel,
  TeamInvitation,
  TeamRole,
  TeamService,
  TeamUser,
} from '../../../services/team/team.service';

type InvitableProfessional = Professional & { hasAccount?: boolean; hasPendingInvitation?: boolean };

/** What each role can do, in the words an owner would use */
const ROLE_HINTS: Record<string, string> = {
  owner: 'Dueño del negocio. Acceso a todo, incluida la membresía.',
  admin: 'Todo lo del negocio: configuración, equipo, finanzas y facturación.',
  manager: 'Agendas, clientes, caja y reportes del día a día.',
  receptionist: 'Agenda citas y atiende a los clientes.',
  practitioner: 'Ve y gestiona su propia agenda y sus clientes.',
  cashier: 'Cobros, cierres de caja y reportes financieros.',
  marketing: 'Promociones, paquetes y mensajes a clientes.',
  viewer: 'Puede ver la información sin cambiar nada.',
};

const PRACTITIONER = 'practitioner';

@Component({
  selector: 'app-users',
  imports: [ReactiveFormsModule, RouterLink, NgxMaskDirective, DatePipe, UpperCasePipe, ConfirmModal],
  templateUrl: './users.html',
  styleUrl: './users.css',
})
export class Users implements OnInit {
  private titleService = inject(PageTitleService);
  private teamService = inject(TeamService);
  private authService = inject(AuthService);
  private businessService = inject(BusinessService);
  private countriesService = inject(CountryService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly users = signal<TeamUser[]>([]);
  readonly invitations = signal<TeamInvitation[]>([]);
  readonly roles = signal<TeamRole[]>([]);
  readonly canInvite = signal(false);
  readonly professionals = signal<InvitableProfessional[]>([]);
  readonly countries = this.countriesService.getStore().getModel();

  readonly formOpen = signal(false);
  readonly sending = signal(false);
  readonly formError = signal<string | null>(null);
  readonly result = signal<{ invitation: CreateInvitationResult; name: string; target: string } | null>(null);
  readonly copiedSlug = signal<string | null>(null);
  readonly toRevoke = signal<TeamInvitation | null>(null);

  readonly roleHint = (code: string) => ROLE_HINTS[code] ?? '';

  form = this.fb.nonNullable.group({
    roleId: ['', Validators.required],
    professionalUserId: [''],
    firstName: ['', [Validators.required, Validators.maxLength(100)]],
    lastName: ['', [Validators.required, Validators.maxLength(100)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    channel: ['whatsapp' as InvitationChannel],
    countryId: [''],
    phone: [''],
  });

  private readonly formValue = signal(this.form.getRawValue());

  readonly selectedRole = computed(() => this.roles().find((r) => r.roleId === this.formValue().roleId) ?? null);
  readonly isPractitioner = computed(() => this.selectedRole()?.code === PRACTITIONER);
  readonly selectedCountry = computed(
    () => this.countries().find((c) => c.countryId === this.formValue().countryId) ?? null
  );

  /** Professionals that still need their own user to sign in */
  readonly professionalsWithoutUser = computed(() => this.professionals().filter((p) => !p.hasAccount));

  readonly pendingInvitations = computed(() => this.invitations().filter((i) => i.status === 'pending'));
  readonly expiredInvitations = computed(() => this.invitations().filter((i) => i.status === 'expired'));

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Usuarios',
      subtitle: 'Quién entra a tu negocio y qué puede hacer',
      breadcrumbs: [{ name: 'Configuración', url: '/settings' }],
    });

    this.countriesService.fetchAndSetEntitiesToStore();
    this.form.valueChanges.subscribe(() => this.formValue.set(this.form.getRawValue()));
    this.form.controls.channel.valueChanges.subscribe(() => this.updatePhoneRequirement());
    this.form.controls.professionalUserId.valueChanges.subscribe((id) => this.fillFromProfessional(id));

    this.load(this.route.snapshot.queryParams['invite'] ?? null);
  }

  load(inviteProfessionalId: string | null = null): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.teamService.getUsers().subscribe({
      next: (res) => {
        this.users.set(res.users);
        this.invitations.set(res.invitations);
        this.roles.set(res.invitableRoles);
        this.canInvite.set(res.canInvite);
        this.loading.set(false);
        if (this.canInvite()) {
          this.loadProfessionals(inviteProfessionalId);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.loadError.set(
          err.status === 403
            ? 'Solo el propietario o un administrador puede ver los usuarios. Si te acaban de dar acceso, cierra sesión y vuelve a entrar.'
            : 'No pudimos cargar los usuarios. Intenta de nuevo.'
        );
      },
    });
  }

  private loadProfessionals(inviteProfessionalId: string | null): void {
    const businessId = this.authService.getUserInfo()?.business_id;
    if (!businessId) return;
    this.businessService
      .getRecordsByBusinessId<InvitableProfessional>(businessId, { page: 1, pageSize: 100 }, 'professionals')
      .subscribe((res: any) => {
        this.professionals.set(res.professionals ?? []);
        if (inviteProfessionalId) {
          this.openForm(inviteProfessionalId);
          // Drop the param so a reload doesn't reopen the form
          this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      });
  }

  // ---- Invite form ----

  openForm(professionalUserId: string | null = null): void {
    this.result.set(null);
    this.formError.set(null);
    this.form.reset({ channel: 'whatsapp', countryId: this.defaultCountryId() });
    if (professionalUserId) {
      const practitioner = this.roles().find((r) => r.code === PRACTITIONER);
      if (practitioner) this.form.controls.roleId.setValue(practitioner.roleId);
      this.form.controls.professionalUserId.setValue(professionalUserId);
    }
    this.updatePhoneRequirement();
    this.formOpen.set(true);
  }

  closeForm(): void {
    this.formOpen.set(false);
    this.result.set(null);
  }

  selectRole(role: TeamRole): void {
    this.form.controls.roleId.setValue(role.roleId);
    if (role.code !== PRACTITIONER) {
      this.form.controls.professionalUserId.setValue('', { emitEvent: false });
    }
  }

  selectChannel(channel: InvitationChannel): void {
    this.form.controls.channel.setValue(channel);
  }

  private fillFromProfessional(professionalUserId: string): void {
    const p = this.professionals().find((x) => x.userId === professionalUserId);
    if (!p) return;
    this.form.patchValue({
      firstName: p.firstName ?? '',
      lastName: p.lastName ?? '',
      email: p.email ?? '',
      phone: p.phone ?? '',
      countryId: p.countryId ?? this.form.controls.countryId.value,
    });
  }

  private updatePhoneRequirement(): void {
    const needsPhone = this.form.controls.channel.value === 'whatsapp';
    for (const control of [this.form.controls.phone, this.form.controls.countryId]) {
      control.setValidators(needsPhone ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private defaultCountryId(): string {
    const me = this.users().find((u) => u.isCurrentUser);
    const fromProfessional = this.professionals().find((p) => p.userId === me?.userId)?.countryId;
    return fromProfessional ?? this.countries()[0]?.countryId ?? '';
  }

  invalid(name: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[name];
    return control.invalid && (control.touched || control.dirty);
  }

  submit(): void {
    if (this.isPractitioner() && !this.form.controls.professionalUserId.value) {
      this.formError.set('Elige a qué profesional le das acceso.');
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formError.set('Revisa los campos marcados.');
      return;
    }

    const v = this.form.getRawValue();
    const phone = v.phone?.trim() || null;
    this.sending.set(true);
    this.formError.set(null);
    this.teamService
      .invite({
        roleId: v.roleId,
        professionalUserId: this.isPractitioner() ? v.professionalUserId : null,
        firstName: v.firstName.trim(),
        lastName: v.lastName.trim(),
        email: v.email.trim(),
        phone,
        countryId: phone ? v.countryId || null : null,
        channel: v.channel,
      })
      .subscribe({
        next: (invitation) => {
          this.sending.set(false);
          const country = this.selectedCountry();
          this.result.set({
            invitation,
            name: v.firstName.trim(),
            target: v.channel === 'whatsapp' ? `${country?.dialCode ?? ''} ${phone}` : v.email.trim(),
          });
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.sending.set(false);
          this.formError.set(this.errorMessage(err));
        },
      });
  }

  // ---- Invitation actions ----

  link(slug: string): string {
    return this.teamService.invitationLink(slug);
  }

  copyLink(slug: string): void {
    navigator.clipboard?.writeText(this.link(slug)).then(() => {
      this.copiedSlug.set(slug);
      setTimeout(() => this.copiedSlug.update((s) => (s === slug ? null : s)), 2000);
    });
  }

  /** Opens WhatsApp with the message ready, for when the automatic one can't be sent */
  whatsAppShareUrl(invitation: { slug: string; firstName: string; role: { name: string } }): string {
    const business = this.authService.getBusinessName() || 'nuestro negocio';
    const text =
      `Hola ${invitation.firstName}, te invito a unirte a ${business} en CitaLink Pro como ${invitation.role.name}. ` +
      `Crea tu contraseña aquí: ${this.link(invitation.slug)}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }

  resultShareUrl(): string {
    const r = this.result();
    const role = this.selectedRole();
    if (!r || !role) return '';
    return this.whatsAppShareUrl({ slug: r.invitation.slug, firstName: r.name, role });
  }

  askRevoke(invitation: TeamInvitation): void {
    this.toRevoke.set(invitation);
  }

  confirmRevoke(): void {
    const invitation = this.toRevoke();
    if (!invitation) return;
    this.teamService.revoke(invitation.userInvitationId).subscribe({
      next: () => {
        this.toRevoke.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.toRevoke.set(null);
        this.loadError.set(this.errorMessage(err));
      },
    });
  }

  // ---- Display helpers ----

  initials(u: { firstName?: string | null; lastName?: string | null }): string {
    return `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}` || '?';
  }

  professionalName(p: Professional): string {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || 'Sin nombre';
  }

  private errorMessage(err: HttpErrorResponse): string {
    const errors: string[] | undefined = err.error?.errors;
    if (errors?.length) return errors.join(' ');
    if (err.status === 403) return 'Solo el propietario o un administrador puede invitar usuarios.';
    return 'No pudimos completar la acción. Intenta de nuevo.';
  }
}
